import { useRef, useState } from "react";
import { Upload, Link, Video, Image } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const isVideo = (url) => url && /\.(mp4|webm|mov|ogv)(\?.*)?$/i.test(url);

/**
 * Media upload input — supports image, GIF, and video files.
 * Uploads to /api/upload/media and returns the hosted URL.
 *
 * Props:
 *   value      string   - current URL value
 *   onChange   fn(url)  - called with the new URL
 *   placeholder string  - input placeholder
 *   className  string   - extra classes on the wrapper div
 */
export default function MediaUploadInput({ value, onChange, placeholder = "https://...", className = "" }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem("it_token");
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/api/upload/media`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(`${API}${res.data.url}`);
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again or paste a URL.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const Icon = value ? (isVideo(value) ? Video : Image) : Link;

  return (
    <div className={`flex gap-2 items-center ${className}`}>
      <div className="relative flex-1 min-w-0">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <input
          type="url"
          value={value || ""}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-orange-500 bg-white text-slate-800"
        />
      </div>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        title="Upload image, GIF, or video"
        className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-all disabled:opacity-50 whitespace-nowrap flex-shrink-0"
      >
        {uploading
          ? <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          : <Upload className="w-3.5 h-3.5" />
        }
        {uploading ? "Uploading…" : "Upload"}
      </button>
      {/* Accept image, gif, and video */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/mp4,video/webm,video/quicktime,.gif"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}
